// src/pages/Auth/AuthPage.jsx
//
// Страницы входа и регистрации как ОТДЕЛЬНЫЕ МАРШРУТЫ (`/login`, `/register`).
//
// ⚠️ ЗАЧЕМ, ЕСЛИ ЕСТЬ МОДАЛКА `LoginWidget`.
//    Модалка живёт внутри Layout и не имеет адреса. Из-за этого нельзя было:
//      • дать ссылку на вход (например из письма или из другого места сайта);
//      • ВЕРНУТЬ человека туда, откуда он пришёл (после входа некуда идти);
//      • открыть страницу входа в новой вкладке.
//    Клиентский путь магазина это ломает: гость оформляет заказ, ему
//    предлагают войти — а войти негде.
//
// 🔑 ПОЧЕМУ ФОРМА ЗДЕСЬ СВОЯ, А НЕ ПЕРЕИСПОЛЬЗОВАН `LoginWidget`.
//    `LoginWidget` — это `Modal` с управлением через `uiSlice`. Вставить его
//    на страницу значило бы открывать модалку ПОВЕРХ страницы входа и
//    дублировать состояние в Redux. Логика обращения к API общая —
//    `useAuth()` → `login`/`register` из `authSlice`, поэтому дублирования
//    сетевого кода нет, только разметка.
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import { useToast } from '../../hooks/useToast.js';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Input } from '../../components/ui/Input/Input.jsx';
import { Breadcrumbs } from '../../components/common/Breadcrumbs/Breadcrumbs';
import { consentAPI, CONSENT_TYPES } from '../../api/consent.js';
import { getAnonymousId } from '../../utils/anonymousId.js';

export const AuthPage = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, isAuthenticated, isLoading } = useAuth();
  const { success, error: toastError } = useToast();

  const isRegister = mode === 'register';
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  // 🔑 СОГЛАСИЕ НА ОБРАБОТКУ ПДн — ГАЛОЧКА ПО УМОЛЧАНИЮ ПУСТАЯ.
  //    Предустановленная галочка = «согласие по умолчанию», которого закон
  //    не допускает: по ч. 1 ст. 9 152-ФЗ согласие должно быть конкретным,
  //    предметным и сознательным. Поэтому `false`, и без неё форма не уйдёт.
  const [consentPd, setConsentPd] = useState(false);

  // Куда вернуться после входа. `Checkout` (и любой другой экран) может
  // передать `state.from`; по умолчанию — на главную.
  const from = location.state?.from?.pathname || '/';

  // Уже вошли — незачем показывать форму.
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, from, navigate]);

  const validate = () => {
    const next = {};
    if (!formData.email) {
      next.email = 'Email обязателен';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = 'Введите корректный email';
    }
    if (!formData.password) {
      next.password = 'Пароль обязателен';
    } else if (formData.password.length < 6) {
      next.password = 'Пароль должен быть не менее 6 символов';
    }
    if (isRegister) {
      if (!formData.first_name) next.first_name = 'Имя обязательно';
      if (!formData.last_name) next.last_name = 'Фамилия обязательна';
      // Без согласия на обработку ПДн регистрация невозможна: мы будем
      // хранить email, имя и телефон — это персональные данные.
      if (!consentPd) next.consent = 'Нужно согласие на обработку персональных данных';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    try {
      if (isRegister) {
        await register({
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
        });
        // 🔑 ФИКСИРУЕМ СОГЛАСИЕ НА СЕРВЕРЕ, А НЕ В localStorage.
        //    По ч. 1 ст. 9 152-ФЗ оператор ОБЯЗАН ДОКАЗАТЬ наличие согласия;
        //    галочка в браузере не доказывает ничего — она исчезает вместе с
        //    очисткой хранилища и серверу не видна.
        //    ⚠️ Ошибку записи НЕ глотаем молча, но и не отменяем регистрацию:
        //    учётная запись уже создана. Сообщаем человеку честно.
        try {
          await consentAPI.accept({
            consent_type: CONSENT_TYPES.PERSONAL_DATA,
            document_version: '1.0',
            anonymous_id: getAnonymousId(),
          });
        } catch (consentErr) {
          console.error('Не удалось зафиксировать согласие:', consentErr);
          toastError('Регистрация прошла, но согласие не сохранилось. Сообщите нам.');
        }
        success('Регистрация прошла успешно!');
      } else {
        await login({ email: formData.email, password: formData.password });
        success('Добро пожаловать!');
      }
      navigate(from, { replace: true });
    } catch (err) {
      const msg = typeof err === 'string' ? err : 'Произошла ошибка';
      setSubmitError(msg);
      toastError(msg);
    }
  };

  const onChange = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <div style={{ backgroundColor: 'rgb(var(--c-bg))', minHeight: '100vh', paddingBottom: '60px' }}>
      <div className="container" style={{ maxWidth: '460px', paddingTop: '32px' }}>
        <Breadcrumbs
          items={[{ label: isRegister ? 'Регистрация' : 'Вход', path: '#' }]}
        />

        <div
          style={{
            backgroundColor: 'rgb(var(--c-surface))',
            borderRadius: '12px',
            padding: '32px',
            marginTop: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>
            {isRegister ? 'Регистрация' : 'Вход в личный кабинет'}
          </h1>
          <p style={{ color: 'rgb(var(--c-text-secondary))', fontSize: '14px', marginBottom: '24px' }}>
            {isRegister
              ? 'Создайте аккаунт, чтобы отслеживать заказы и сохранять расчёты.'
              : 'Войдите, чтобы видеть свои заказы и историю.'}
          </p>

          {submitError && (
            <div
              style={{
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                color: '#B91C1C',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '14px',
                marginBottom: '16px',
              }}
            >
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {isRegister && (
              <>
                <Input
                  label="Имя"
                  value={formData.first_name}
                  onChange={onChange('first_name')}
                  error={errors.first_name}
                  placeholder="Владимир"
                />
                <div style={{ height: '16px' }} />
                <Input
                  label="Фамилия"
                  value={formData.last_name}
                  onChange={onChange('last_name')}
                  error={errors.last_name}
                  placeholder="Цар"
                />
                <div style={{ height: '16px' }} />
                <Input
                  label="Телефон"
                  value={formData.phone}
                  onChange={onChange('phone')}
                  placeholder="+7 904 095 01 10"
                />
                <div style={{ height: '16px' }} />
              </>
            )}

            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={onChange('email')}
              error={errors.email}
              placeholder="you@example.com"
            />
            <div style={{ height: '16px' }} />
            <Input
              label="Пароль"
              type="password"
              value={formData.password}
              onChange={onChange('password')}
              error={errors.password}
              placeholder="Не менее 6 символов"
            />

            {/* 🔑 СОГЛАСИЕ НА ОБРАБОТКУ ПЕРСОНАЛЬНЫХ ДАННЫХ.
                Показываем ТОЛЬКО при регистрации: при входе новых данных не
                собирается, и спрашивать заново нечего.

                ⚠️ ГАЛОЧКА ПО УМОЛЧАНИЮ ПУСТАЯ. Предустановленная отметка —
                это «согласие по умолчанию», которого закон не допускает
                (ч. 1 ст. 9 152-ФЗ: согласие должно быть конкретным,
                предметным и сознательным).
                ⚠️ БЕЗ ГАЛОЧКИ ФОРМА НЕ УЙДЁТ (`validate`). Иначе согласие
                не собрано, а данные уже сохранены — то есть нарушение. */}
            {isRegister && (
              <>
                <div style={{ height: '20px' }} />
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    fontSize: '13px',
                    color: 'rgb(var(--c-text-secondary))',
                    lineHeight: 1.5,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={consentPd}
                    onChange={(e) => setConsentPd(e.target.checked)}
                    style={{ marginTop: '3px', flexShrink: 0 }}
                  />
                  <span>
                    Я согласен на обработку персональных данных в соответствии
                    с{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'rgb(var(--c-accent))', fontWeight: 600 }}
                    >
                      политикой конфиденциальности
                    </Link>
                  </span>
                </label>
                {errors.consent && (
                  <div style={{ color: '#DC2626', fontSize: '13px', marginTop: '6px' }}>
                    {errors.consent}
                  </div>
                )}
              </>
            )}

            <div style={{ height: '24px' }} />
            <Button type="submit" fullWidth isLoading={isLoading}>
              {isRegister ? 'Зарегистрироваться' : 'Войти'}
            </Button>
          </form>

          <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '14px' }}>
            {isRegister ? (
              <>
                Уже есть аккаунт?{' '}
                <Link to="/login" style={{ color: 'rgb(var(--c-accent))', fontWeight: 600 }}>
                  Войти
                </Link>
              </>
            ) : (
              <>
                Нет аккаунта?{' '}
                <Link to="/register" style={{ color: 'rgb(var(--c-accent))', fontWeight: 600 }}>
                  Зарегистрироваться
                </Link>
              </>
            )}
          </div>
        </div>

        {/* ⚠️ ЧЕСТНОЕ ПРЕДУПРЕЖДЕНИЕ. Письма сейчас НЕ отправляются:
            `nodemailer` в проекте отсутствует, а отправка в
            `authController.requestPasswordReset` закомментирована.
            Поэтому восстановление пароля НЕ показываем — иначе обещали бы
            действие, которого нет. См. ARCHITECTURE_TASKS.txt. */}
        <p
          style={{
            marginTop: '16px',
            fontSize: '13px',
            color: 'rgb(var(--c-text-muted))',
            textAlign: 'center',
          }}
        >
          Забыли пароль? Напишите нам — восстановим вручную.
        </p>
      </div>
    </div>
  );
};

export const LoginPage = () => <AuthPage mode="login" />;
export const RegisterPage = () => <AuthPage mode="register" />;
